import { useErrorMessage } from '@/composables/useErrorMessage';
import type { NotificationInterface } from '@/interfaces/components/notification/notification.interface';
import type { Axios, AxiosRequestConfig, Method, ResponseType } from 'axios';

/**
 * Trata requisições HTTP com manipulação de erros centralizada
 *
 * @template ReturnType Tipo de retorno da requisição
 * @template ParamsType Tipo dos parâmetros da URL
 * @template BodyType Tipo do corpo da requisição
 *
 * @param {Axios} service Instância do Axios para realizar a requisição
 * @param {Method} method Método HTTP da requisição
 * @param {string} url Endereço do endpoint
 * @param {Params} [params] Parâmetros opcionais da URL
 * @param {Body} [body] Corpo da requisição
 * @param {ResponseType}[responseType] Tipo de resposta esperado
 *
 * @returns {Promise<{ data: Return, notification: NotificationInterface }>} Dados da resposta da requisição
 *
 * @throws {Error} Erro tratado pelo useErrorMessage em caso de falha
 *
 * @example
 * const data = await useRequestHandler<ReturnType, ParamsType, BodyType>(
 *   axiosInstance,
 *   'POST',
 *   `/api/pipelines/${id}/search`,
 *   params,
 *   body
 * )
 */

/**
 * @deprecated Use o novo arquivo que esta presente em utils/useRequestHandler.ts
 */
export const useRequestHandler = async <Return, Params, Body>(
  service: Axios,
  method: Method,
  url: string,
  params?: Params,
  body?: Body,
  responseType?: ResponseType,
  extraConfig?: Partial<Pick<AxiosRequestConfig, 'headers'>>,
): Promise<{ data: Return } | { notification: NotificationInterface }> => {
  try {
    const { data } = await service.request<Return>({
      url,
      params,
      method,
      data: body,
      responseType,
      ...extraConfig,
    });

    return { data };
  } catch (error) {
    return useErrorMessage(
      error,
      'Falha desconhecida, atualize a página e tente novamente!',
    );
  }
};
